import { ChangeDetectionStrategy, Component, DestroyRef, type InputSignal, type OnInit, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, type AbstractControl, type FormControl, type FormGroup } from '@angular/forms';
import { PhoneTypeEnum } from '@pecan/api/enums';
import { MatFormField, MatInput, MatError } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxTrimDirectiveModule } from 'ngx-trim-directive';
import { EnumUtils } from '@pecan/shared/models/enum-utils';
import { InvalidLabelComponent } from '@pecan/shared/components/invalid-label/invalid-label.component';
import { MAX_FIELD_LENGTH_DICTIONARY } from '@pecan/shared/constants/max-field-length-dictionary.constant';
import type { IPhoneFormGroup } from '../../models/phone-form-group.interface';
import type { IPhoneInfoFormValue } from '../../models/phone-info-form-value.interface';
import { PhoneTypeNamePipe } from '../../pipes/phone-type-name.pipe';
import { InternationalPhoneInputComponent } from '../international-phone-input/international-phone-input.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        InvalidLabelComponent,
        MatFormField,
        MatInput,
        MatError,
        MatSelect,
        MatOption,
        NgxTrimDirectiveModule,
        TranslatePipe,
        InternationalPhoneInputComponent,
        PhoneTypeNamePipe,
    ],
    selector: 'app-phone-info',
    styleUrls: ['./phone-info.component.scss'],
    templateUrl: './phone-info.component.html',
})
export class PhoneInfoComponent implements OnInit {
    public readonly group: InputSignal<FormGroup<IPhoneFormGroup>> = input.required<FormGroup<IPhoneFormGroup>>();

    public readonly showPrimaryInfo: InputSignal<boolean> = input<boolean>(false);

    public maxLengthDictionary: typeof MAX_FIELD_LENGTH_DICTIONARY = MAX_FIELD_LENGTH_DICTIONARY;

    public get phoneInfoControl(): FormControl<IPhoneInfoFormValue> {
        return this.group()?.controls.phoneInfo;
    }

    public get extControl(): AbstractControl {
        return this.group()?.controls.ext;
    }

    public get isPrimaryControl(): AbstractControl {
        return this.group()?.controls.isPrimary;
    }

    public get type(): AbstractControl {
        return this.group()?.controls.type;
    }

    public readonly types: number[] = EnumUtils.getValues(PhoneTypeEnum);

    private _destroyRef: DestroyRef = inject(DestroyRef);

    public ngOnInit(): void {
        this._ignoreCharsForControl(this.extControl);
    }

    private _ignoreCharsForControl(control: AbstractControl): void {
        control.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value: string) => {
            const filteredValue = value?.replace(/[^0-9]/g, '');
            control.setValue(filteredValue, { emitEvent: false });
        });
    }
}
